import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Target, Layers, ChevronRight, X } from "lucide-react";

export function AdminArchetypesPage() {
    const [archetypes, setArchetypes] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const { api } = useAuth();

    useEffect(() => {
        api.get("/v2/admin/library/archetypes").then((r) => {
            setArchetypes(r.data.archetypes);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const openDetail = async (id) => {
        const r = await api.get(`/v2/admin/library/archetypes/${id}`);
        setSelected(r.data);
    };

    const categories = [...new Set(archetypes.map((a) => a.category || "Other"))].sort();
    const filtered = filter ? archetypes.filter((a) => a.category === filter) : archetypes;

    return (
        <div className="p-8" data-testid="admin-archetypes">
            <div className="mb-8">
                <h1 className="text-2xl font-display font-bold text-white">Role Archetypes</h1>
                <p className="text-sm text-gray-500 mt-1">Cross-customer role taxonomy — the data moat. {archetypes.length} archetypes, each with canonical tasks and AS/AL/JD scores.</p>
            </div>

            {/* Category filter */}
            <div className="flex gap-2 mb-6 flex-wrap">
                <button onClick={() => setFilter("")} data-testid="filter-all"
                    className={`px-3 py-1.5 text-xs border transition-colors ${filter === "" ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                    All ({archetypes.length})
                </button>
                {categories.map((c) => (
                    <button key={c} onClick={() => setFilter(c)}
                        className={`px-3 py-1.5 text-xs border transition-colors ${filter === c ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                        {c} ({archetypes.filter((a) => a.category === c).length})
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-sm text-gray-600">Loading archetype library...</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((a, i) => (
                        <motion.button key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                            onClick={() => openDetail(a.id)}
                            className="text-left bg-card border border-border/40 p-5 hover:border-white/20 transition-colors group" data-testid={`archetype-card-${a.slug}`}>
                            <div className="flex items-start gap-3 mb-3">
                                <Target className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-white truncate">{a.name}</h3>
                                    <p className="text-xs text-gray-600 font-mono">{a.category}</p>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-2 mb-3">{a.description}</p>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600">{a.task_count} tasks</span>
                                <span className="text-gray-600">{(a.suggested_modules || []).length} modules</span>
                            </div>
                        </motion.button>
                    ))}
                </div>
            )}

            {/* Detail modal */}
            {selected && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setSelected(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">{selected.name}</h2>
                                <p className="text-xs text-gray-500 font-mono mt-1">{selected.category} &middot; v{selected.version}</p>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-gray-600 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            <p className="text-sm text-gray-400">{selected.description}</p>

                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Canonical Tasks ({selected.canonical_tasks?.length || 0})</h3>
                                <div className="space-y-2">
                                    {(selected.canonical_tasks || []).map((t, i) => (
                                        <div key={i} className="bg-muted border border-border/40 p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-white font-semibold">{t.task_name}</span>
                                                <span className="text-xs text-gray-600 font-mono">{t.time_share_pct}%</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                <div><span className="text-gray-600">AS</span> <span className="text-white font-mono ml-1">{t.automation_susceptibility}</span></div>
                                                <div><span className="text-gray-600">AL</span> <span className="text-white font-mono ml-1">{t.augmentation_leverage}</span></div>
                                                <div><span className="text-gray-600">JD</span> <span className="text-white font-mono ml-1">{t.judgment_density}</span></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Suggested Modules</h3>
                                <div className="flex gap-2 flex-wrap">
                                    {(selected.suggested_modules || []).map((m) => (
                                        <span key={m} className="px-2 py-1 text-xs font-mono bg-white/[0.04] border border-white/10 text-gray-300">{m}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}


export function AdminModulesPage() {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const { api } = useAuth();

    useEffect(() => {
        api.get("/v2/admin/library/modules").then((r) => {
            setModules(r.data.modules);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    return (
        <div className="p-8" data-testid="admin-modules">
            <div className="mb-8">
                <h1 className="text-2xl font-display font-bold text-white">Assessment Modules</h1>
                <p className="text-sm text-gray-500 mt-1">The module library — TDA, MV, TA, Aspiration, Tool Exposure, Strategic Lock-In. Customers enable modules per tier.</p>
            </div>

            {loading ? (
                <p className="text-sm text-gray-600">Loading modules...</p>
            ) : (
                <div className="space-y-3">
                    {modules.map((m, i) => (
                        <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                            className="bg-card border border-border/40 p-5" data-testid={`module-card-${m.type_tag}`}>
                            <div className="flex items-start gap-4">
                                <Layers className="w-5 h-5 text-gray-600 mt-1" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="text-sm font-semibold text-white">{m.name}</h3>
                                        <span className="text-xs font-mono text-gray-600 px-1.5 py-0.5 border border-border/40">{m.type_tag}</span>
                                        {m.is_standard && <span className="text-xs text-gray-600">standard</span>}
                                    </div>
                                    <p className="text-xs text-gray-500 mb-3">{m.description}</p>
                                    <div className="flex gap-4 text-xs text-gray-600">
                                        <span>respondent: <span className="text-gray-300 font-mono">{m.respondent}</span></span>
                                        <span>contribution: <span className="text-gray-300 font-mono">{m.scoring_contribution}</span></span>
                                        <span>weight: <span className="text-gray-300 font-mono">{m.scoring_weight}</span></span>
                                        <span>tier: <span className="text-gray-300 font-mono">{m.tier_gate}</span></span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}


export function AdminHealthPagePlaceholder() {
    return null; // Real implementation moved to AdminHealthPage.js
}
